import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";

import { Chess, type Move, type Square } from "chess.js";

import {
  evalShareForWhite,
  formatStockfishScore,
} from "@/pages/analysis/engine/stockfishProtocol";
import type { StockfishPv } from "@/pages/analysis/engine/stockfishTypes";
import { formatStockfishPvSan } from "@/pages/analysis/engine/stockfishView";
import { useStockfishEvaluation } from "@/pages/analysis/engine/useStockfishEvaluation";
import { lichessBoardPalette } from "@/shared/config/siteContent";
import { useI18n } from "@/shared/i18n/LanguageContext";
import { replayUciMove, resolveUciMove } from "@/shared/ui/chessMove";
import { ChessSurface } from "@/shared/ui/ChessSurface";

import { AnalysisModelChoicePanel } from "./AnalysisModelChoicePanel";
import { StockfishAnalysisPanel } from "./StockfishAnalysisPanel";

type BoardOrientation = "white" | "black";

function buildGame() {
  return new Chess();
}

function moveToUci(move: Move) {
  return `${move.from}${move.to}${move.promotion ?? ""}`;
}

function rebuildGame(moves: Move[], count: number) {
  const next = buildGame();
  for (const move of moves.slice(0, count)) {
    replayUciMove(next, moveToUci(move));
  }
  return next;
}

function buildMovePairs(history: Move[]) {
  const pairs: Array<{
    moveNumber: number;
    white?: Move;
    black?: Move;
    whitePly: number;
    blackPly: number;
  }> = [];
  for (let index = 0; index < history.length; index += 2) {
    pairs.push({
      moveNumber: Math.floor(index / 2) + 1,
      white: history[index],
      black: history[index + 1],
      whitePly: index + 1,
      blackPly: index + 2,
    });
  }
  return pairs;
}

async function copyTextToClipboard(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "true");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();

  try {
    if (!document.execCommand("copy")) {
      throw new Error("Copy command failed");
    }
  } finally {
    document.body.removeChild(textarea);
  }
}

export function AnalysisPage() {
  const { copy } = useI18n();
  const [game, setGame] = useState(() => buildGame());
  const [selectedSquare, setSelectedSquare] = useState<Square | null>(null);
  const [legalTargets, setLegalTargets] = useState<Square[]>([]);
  const [orientation, setOrientation] = useState<BoardOrientation>("white");
  const [reviewPly, setReviewPly] = useState<number | null>(null);
  const [boardWidth, setBoardWidth] = useState(0);
  const [expandedPv, setExpandedPv] = useState<number | null>(null);
  const [showAllEngineLines, setShowAllEngineLines] = useState(false);
  const [fenCopyState, setFenCopyState] = useState<"idle" | "copied" | "failed">("idle");
  const boardWrapRef = useRef<HTMLDivElement | null>(null);
  const fenCopyTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const node = boardWrapRef.current;
    if (!node) {
      return;
    }

    const updateWidth = () => {
      const isCompact = window.matchMedia("(max-width: 520px)").matches;
      const boardChromeWidth = isCompact ? 42 : 54;
      const next = Math.floor(Math.min(Math.max(node.clientWidth - boardChromeWidth, 220), 620));
      setBoardWidth(next > 0 ? next : 560);
    };

    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const history = useMemo(
    () => game.history({ verbose: true }) as Move[],
    [game],
  );
  const movePairs = useMemo(() => buildMovePairs(history), [history]);
  const displayPly = reviewPly ?? history.length;
  const displayGame = useMemo(
    () => rebuildGame(history, displayPly),
    [displayPly, history],
  );
  const displayHistory = useMemo(
    () => displayGame.history({ verbose: true }) as Move[],
    [displayGame],
  );
  const displayMoves = useMemo(
    () => displayHistory.map(moveToUci),
    [displayHistory],
  );
  const lastMove = displayHistory.at(-1) ?? null;
  const currentFen = displayGame.fen();
  const {
    enabled: engineEnabled,
    setEnabled: setEngineEnabled,
    status: engineStatus,
    evaluation: engineEvaluation,
  } = useStockfishEvaluation({
    fen: currentFen,
    depth: 14,
    multiPv: 5,
  });

  const openPly = useCallback(
    (ply: number) => {
      const nextPly = Math.max(0, Math.min(ply, history.length));
      setReviewPly(nextPly === history.length ? null : nextPly);
      setSelectedSquare(null);
      setLegalTargets([]);
    },
    [history.length],
  );

  const goToStart = useCallback(() => openPly(0), [openPly]);
  const goToLatest = useCallback(() => openPly(history.length), [history.length, openPly]);
  const goToPrev = useCallback(
    () => openPly(Math.max(0, displayPly - 1)),
    [displayPly, openPly],
  );
  const goToNext = useCallback(
    () => openPly(Math.min(history.length, displayPly + 1)),
    [displayPly, history.length, openPly],
  );
  const flipBoard = useCallback(() => {
    setOrientation((current) => (current === "white" ? "black" : "white"));
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.isContentEditable
      ) {
        return;
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        goToStart();
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        goToPrev();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        goToNext();
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        goToLatest();
      } else if (event.key.toLowerCase() === "f") {
        event.preventDefault();
        flipBoard();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [flipBoard, goToLatest, goToNext, goToPrev, goToStart]);

  useEffect(() => {
    setExpandedPv(null);
  }, [currentFen, engineEnabled]);

  useEffect(() => {
    setFenCopyState("idle");
  }, [currentFen]);

  useEffect(
    () => () => {
      if (fenCopyTimeoutRef.current !== null) {
        window.clearTimeout(fenCopyTimeoutRef.current);
      }
    },
    [],
  );

  const copyCurrentFen = useCallback(async () => {
    if (fenCopyTimeoutRef.current !== null) {
      window.clearTimeout(fenCopyTimeoutRef.current);
    }

    try {
      await copyTextToClipboard(currentFen);
      setFenCopyState("copied");
    } catch {
      setFenCopyState("failed");
    }

    fenCopyTimeoutRef.current = window.setTimeout(() => {
      setFenCopyState("idle");
      fenCopyTimeoutRef.current = null;
    }, 1400);
  }, [currentFen]);

  const commitMove = (from: Square, to: Square) => {
    const next = rebuildGame(history, displayPly);
    const moveUci = resolveUciMove(next, from, to);
    if (!moveUci) {
      return false;
    }

    replayUciMove(next, moveUci);
    setGame(next);
    setReviewPly(null);
    setSelectedSquare(null);
    setLegalTargets([]);
    return true;
  };

  const commitUciMove = useCallback(
    (uci: string) => {
      const next = rebuildGame(history, displayPly);
      try {
        const move = replayUciMove(next, uci);
        if (!move) {
          return false;
        }
      } catch {
        return false;
      }

      setGame(next);
      setReviewPly(null);
      setSelectedSquare(null);
      setLegalTargets([]);
      return true;
    },
    [displayPly, history],
  );

  const selectSquare = (square: Square) => {
    const moves = displayGame.moves({ square, verbose: true });
    if (moves.length === 0) {
      setSelectedSquare(null);
      setLegalTargets([]);
      return;
    }

    setSelectedSquare(square);
    setLegalTargets(moves.map((move) => move.to));
  };

  const onSquareClick = (squareName: Square) => {
    if (selectedSquare) {
      if (squareName === selectedSquare) {
        setSelectedSquare(null);
        setLegalTargets([]);
        return;
      }

      if (commitMove(selectedSquare, squareName)) {
        return;
      }
    }

    const piece = displayGame.get(squareName);
    if (!piece || piece.color !== displayGame.turn()) {
      setSelectedSquare(null);
      setLegalTargets([]);
      return;
    }

    selectSquare(squareName);
  };

  const onPieceDrop = (sourceSquare: Square, targetSquare?: Square) => {
    if (!targetSquare) {
      return false;
    }
    return commitMove(sourceSquare, targetSquare);
  };

  const onPieceDragBegin = (_piece: string, sourceSquare: Square) => {
    selectSquare(sourceSquare);
  };

  const customSquareStyles = useMemo(() => {
    const styles: Record<string, CSSProperties> = {};

    if (selectedSquare) {
      styles[selectedSquare] = {
        boxShadow: lichessBoardPalette.selected,
      };
    }

    for (const square of legalTargets) {
      styles[square] = {
        ...(styles[square] ?? {}),
        background: lichessBoardPalette.legalTarget,
      };
    }

    if (lastMove) {
      styles[lastMove.from] = {
        ...(styles[lastMove.from] ?? {}),
        backgroundColor: lichessBoardPalette.lastMove,
      };
      styles[lastMove.to] = {
        ...(styles[lastMove.to] ?? {}),
        backgroundColor: lichessBoardPalette.lastMove,
      };
    }

    if (displayGame.inCheck()) {
      const boardState = displayGame.board();
      for (let rankIndex = 0; rankIndex < boardState.length; rankIndex += 1) {
        for (let fileIndex = 0; fileIndex < boardState[rankIndex].length; fileIndex += 1) {
          const piece = boardState[rankIndex][fileIndex];
          if (piece && piece.type === "k" && piece.color === displayGame.turn()) {
            const file = String.fromCharCode(97 + fileIndex);
            const rank = `${8 - rankIndex}`;
            const kingSquare = `${file}${rank}` as Square;
            styles[kingSquare] = {
              ...(styles[kingSquare] ?? {}),
              boxShadow: lichessBoardPalette.check,
            };
          }
        }
      }
    }

    return styles;
  }, [displayGame, lastMove, legalTargets, selectedSquare]);

  const fenLabel = displayGame.fen().split(" ").slice(0, 4).join(" ");
  const currentEvaluation =
    engineEvaluation?.fen === currentFen ? engineEvaluation : null;
  const enginePvs = currentEvaluation?.pvs ?? [];
  const primaryPv = enginePvs[0];
  const engineScoreLabel = !engineEnabled
    ? "OFF"
    : primaryPv
      ? formatStockfishScore(primaryPv, displayGame.turn())
      : engineStatus === "error" || engineStatus === "unsupported"
        ? "ERR"
        : "...";
  const evalShare = engineEnabled ? evalShareForWhite(primaryPv, displayGame.turn()) : 50;
  const evalBarStyle = {
    "--white-share": `${evalShare}%`,
  } as CSSProperties;
  const evalTooltipHint = (() => {
    if (!engineEnabled) {
      return copy.analysis.engineOff;
    }
    if (engineScoreLabel === "..." || engineScoreLabel === "ERR") {
      return engineScoreLabel === "ERR" ? copy.analysis.engineUnavailable : copy.analysis.calculating;
    }
    if (engineScoreLabel.startsWith("+") || engineScoreLabel.startsWith("#+")) {
      return copy.analysis.whiteBetter;
    }
    if (engineScoreLabel.startsWith("-") || engineScoreLabel.startsWith("#-")) {
      return copy.analysis.blackBetter;
    }
    return copy.analysis.even;
  })();
  const visibleEngineLineCount = showAllEngineLines ? 5 : 3;
  const engineLines = engineEnabled
    ? Array.from({ length: visibleEngineLineCount }, (_, index) => {
        const pv = enginePvs[index] as StockfishPv | undefined;
        return {
          rank: index + 1,
          eval: pv ? formatStockfishScore(pv, displayGame.turn()) : "...",
          variation: pv ? formatStockfishPvSan(currentFen, pv.pv) : "...",
          hasPv: Boolean(pv),
        };
      })
    : [];
  const canStepBack = displayPly > 0;
  const canStepForward = displayPly < history.length;
  const boardStageStyle =
    boardWidth > 0
      ? ({ "--analysis-board-size": `${boardWidth}px` } as CSSProperties)
      : undefined;

  return (
    <div className="page-stack analysis-page">
      <section className="analysis-room" aria-label={copy.analysis.workspace}>
        <aside className="session-rail session-rail--status analysis-engine-rail">
          <StockfishAnalysisPanel
            enabled={engineEnabled}
            status={engineStatus}
            scoreLabel={engineScoreLabel}
            lines={engineLines}
            expandedLine={expandedPv}
            showAllLines={showAllEngineLines}
            onToggleEnabled={() => setEngineEnabled((current) => !current)}
            onToggleLine={(rank) =>
              setExpandedPv((current) => (current === rank ? null : rank))
            }
            onToggleShowAllLines={() => setShowAllEngineLines((current) => !current)}
          />
          <AnalysisModelChoicePanel
            moves={displayMoves}
            fen={null}
            positionKey={currentFen}
            onSelectMove={commitUciMove}
          />
        </aside>

        <main className="analysis-board-column">
          <div className="analysis-board-shell">
            <div className="analysis-board-stage" style={boardStageStyle}>
              <div className="analysis-board-surface" ref={boardWrapRef}>
                {boardWidth > 0 ? (
                  <ChessSurface
                    id="catie-analysis-board"
                    boardWidth={boardWidth}
                    position={displayGame.fen()}
                    orientation={orientation}
                    arePiecesDraggable={!displayGame.isGameOver()}
                    onPieceDrop={onPieceDrop}
                    onPieceDragBegin={onPieceDragBegin}
                    onSquareClick={onSquareClick}
                    customSquareStyles={customSquareStyles}
                    leftBoardSlot={
                      <div
                        className="analysis-board-eval"
                        data-orientation={orientation}
                        style={evalBarStyle}
                        aria-label={`Evaluation ${engineScoreLabel}`}
                        tabIndex={0}
                      >
                        <div className="analysis-board-eval__white" />
                        <div className="analysis-board-eval__black" />
                        <span className="analysis-board-eval__tooltip" aria-hidden="true">
                          <strong>{engineScoreLabel}</strong>
                          <small>{evalTooltipHint}</small>
                        </span>
                      </div>
                    }
                    darkSquareColor={lichessBoardPalette.dark}
                    lightSquareColor={lichessBoardPalette.light}
                  />
                ) : null}
              </div>
            </div>
          </div>

          <div className="analysis-review-bar" aria-label={copy.analysis.controls}>
            <div className="analysis-review-controls">
              <button type="button" disabled={!canStepBack} onClick={goToStart} aria-label={copy.analysis.initial}>
                |&lt;
              </button>
              <button type="button" disabled={!canStepBack} onClick={goToPrev} aria-label={copy.analysis.previous}>
                &lt;
              </button>
              <button type="button" disabled={!canStepForward} onClick={goToNext} aria-label={copy.analysis.next}>
                &gt;
              </button>
              <button type="button" disabled={!canStepForward} onClick={goToLatest} aria-label={copy.analysis.latest}>
                &gt;|
              </button>
              <button type="button" onClick={flipBoard} aria-label={copy.analysis.flip}>
                {copy.analysis.flip}
              </button>
            </div>
          </div>

          <div className="session-clock session-clock--bottom analysis-position-strip">
            <span className="session-clock__side">FEN</span>
            <span className="analysis-position-strip__fen" title={currentFen}>
              {fenLabel}
            </span>
            <button
              className="analysis-position-strip__copy"
              type="button"
              onClick={copyCurrentFen}
              aria-label={copy.analysis.copyFen}
            >
              {fenCopyState === "copied"
                ? copy.analysis.copied
                : fenCopyState === "failed"
                  ? copy.analysis.failed
                  : copy.analysis.copy}
            </button>
          </div>
        </main>

        <aside className="session-rail session-rail--moves analysis-move-rail">
          <div className="session-rail__head">
            <p className="session-rail__eyebrow">{copy.analysis.moveHistory}</p>
          </div>

          <div className="move-log analysis-move-log" aria-label={copy.analysis.moveHistory}>
            {movePairs.map((pair) => (
              <div className="move-log__row" key={pair.moveNumber}>
                <span className="move-log__index">{pair.moveNumber}.</span>
                <span className="move-log__move">
                  {pair.white ? (
                    <button
                      className={
                        displayPly === pair.whitePly
                          ? "move-log__move-button move-log__move-button--active"
                          : "move-log__move-button"
                      }
                      type="button"
                      onClick={() => openPly(pair.whitePly)}
                      aria-label={`View position after ${pair.moveNumber}. ${pair.white.san}`}
                    >
                      {pair.white.san}
                    </button>
                  ) : null}
                </span>
                <span className="move-log__move">
                  {pair.black ? (
                    <button
                      className={
                        displayPly === pair.blackPly
                          ? "move-log__move-button move-log__move-button--active"
                          : "move-log__move-button"
                      }
                      type="button"
                      onClick={() => openPly(pair.blackPly)}
                      aria-label={`View position after ${pair.moveNumber}... ${pair.black.san}`}
                    >
                      {pair.black.san}
                    </button>
                  ) : (
                    <span className="move-log__move-placeholder" />
                  )}
                </span>
              </div>
            ))}
          </div>
        </aside>
      </section>
    </div>
  );
}
